/**
 * Default assessment factors for the standard 9-area risk taxonomy.
 *
 * Keyed by `"AreaName::ContributorName"` (case-insensitive lookup via
 * `getDefaultFactors`). Used as a fallback when a contributor in the DB
 * predates the `factors` field and has an empty value.
 */

const FACTORS_MAP: Record<string, string[]> = {
  // 1. Customer
  "customer::commitment": [
    "Customer's commitment to project's success",
    "Designated Project Manager from Customer",
    "Customer's Org. geared to imbibe the new system",
    "Clarity of direction",
    "Funding / Budget",
  ],
  "customer::knowledge": [
    "Customer's knowledge in the area of: Business / Application",
    "Customer's knowledge in Technology",
    "Customer's knowledge in Development Methodology",
  ],
  "customer::dependency": [
    "Timeliness and/or Quality of Sign-off & Reviews",
    "Timeliness and/or Quality of Decisions on technical aspects",
    "Timeliness and/or Quality of Documents",
    "Timeliness and/or Quality of Reusable components",
  ],
  "customer::attitude": [
    "Understanding", "Participative", "Reasonable",
    "Responsiveness", "Micro management", "Expectation Level",
  ],
  "customer::location": [
    "Communication channels between our organization and customer's organization",
    "Language of communication",
    "Currency convertibility",
    "Getting work permit/visas",
    "Infrastructure facilities in customer's country/city",
  ],
  "customer::hardware": [
    "Target hardware platform is in use for <xyz> years",
    "Hardware to be upgraded and is / is not upgradeable",
    "Hardware planned to be procured and will be installed by <dd-mon-yyyy>",
  ],
  "customer::system software": [
    "Target system software is in use for <xyz> years",
    "System software to be upgraded and is / is not upgradeable",
    "System software planned to be procured and will be installed by <dd-mon-yyyy>",
  ],
  "customer::miscellaneous": [
    "Any items pertaining to Customer characteristics not covered above",
  ],

  // 2. Organization
  "organization::premises": [
    "Disruptions", "Safety related hazards", "Night time support",
  ],
  "organization::facilities": [
    "Adequacy and timeliness of Sitting arrangement",
    "Adequacy and timeliness of Phone",
    "Adequacy and timeliness of Telecon",
    "Adequacy and timeliness of Videocon",
    "Adequacy and timeliness of Laptops for support",
    "Adequacy and timeliness of Mobiles/pagers",
    "Adequacy and timeliness of Electricity Supply",
  ],
  "organization::hardware": [
    "Available within the organization",
    "Procurement Status and likelihood of delay",
    "Available configuration v/s desirable",
  ],
  "organization::system software": [
    "Available within the organization",
    "Procurement Status and likelihood of delay",
    "Available configuration v/s desirable",
  ],
  "organization::productivity tools": [
    "Available within the organization",
    "Procurement Status and likelihood of delay",
    "Skills, training and experience",
  ],
  "organization::human resources": [
    "Timeliness of getting required skilled resources",
    "Stability / Attrition",
    "Preferred/un-preferred timings/location/role",
    "Rotation/release due",
  ],
  "organization::sqa": [
    "Knowledge and experience of CMM, Organization's Standards and Procedures",
    "SQA's effort availability",
    "Involvement of SQA in status reviews, technical reviews, and sample/system testing",
  ],
  "organization::miscellaneous": [
    "Any items pertaining to the Organization characteristics not covered above",
  ],

  // 3. Project
  "project::size/effort": [
    "Effort Involved", "Number of users", "Number of installation sites",
    "Outstanding Problems/Enhancements", "Volume of Problems/Enhancements",
    "Underestimated",
  ],
  "project::schedule": [
    "Dictated by customer / upper management",
    "Schedule is not in balance with resources / requirements",
    "No flexibility in terms of phased delivery",
    "Optimistic - \"best case\" rather than \"expected case\"",
    "Schedule depends on individuals",
    "SLA terms very stringent",
    "Underestimated",
  ],
  "project::criticality": [
    "Is the implementation of our project related to the successful implementation of yet another project?",
    "Is the project in the \"mission critical\" category for the prospect or customer?",
  ],
  "project::intergroup dependency": [
    "Number of parties involved",
    "Communication mechanism and frequency",
    "Coordination", "Escalation Mechanism",
    "Degree of Dependency", "Clarity of role of each party",
  ],
  "project::information security": [
    "Version Control Checklist Compliance",
    "Source / Data Backup", "Access Rights",
    "Version Control System (Tool, Model, Branches)",
    "Build Process Control", "Release Management",
  ],
  "project::miscellaneous": [
    "Any items pertaining to Project characteristics not covered above",
  ],

  // 4. Requirements
  "requirements::stability and quality": [
    "Clarity", "Sign-off", "Outstanding issues / clarifications",
    "Creep and Size of Changes", "Assumptions",
    "Well defined acceptance criteria",
  ],
  "requirements::volumes & performance": [
    "Peak loads", "Response Time", "Time window availability",
  ],
  "requirements::interfaces": [
    "Number of interfaces", "Batch / online", "Update / Read only",
    "Changes expected in interfacing systems",
    "Dependency on interfacing systems",
  ],
  "requirements::special requirements": [
    "Statutory and legal requirements",
    "Security requirements", "Whether keep changing",
  ],
  "requirements::miscellaneous": [
    "Any items pertaining to the characteristics of Requirements not covered above",
  ],

  // 5. Team
  "team::training": [
    "Very high training requirements",
    "Availability of training within the Organization/Same City/India/Abroad",
    "Timeliness and quality of training, Learning curve",
    "Well defined induction/on the job training mechanism",
  ],
  "team::cohesiveness": [
    "Team Size", "Motivation and Morale", "Team Goals", "Geography",
  ],
  "team::project management expertise": [
    "Experience level of PM/PL/TL",
    "Usage of planning and tracking tools",
    "Balancing of team structure",
    "Business communication skills",
  ],
  "team::analysis expertise": [
    "Application knowledge within the project team/organization",
    "Number of analysts", "Analysis & system testing skills",
  ],
  "team::design expertise": [
    "Platform knowledge within the project team/organization",
    "Number of designers", "Design and integration skills",
  ],
  "team::programming expertise": [
    "Programming knowledge within the project team/organization on the chosen language",
    "Number of programmers and independent testers",
    "Programming and unit testing skills",
  ],
  "team::vendor personnel": [
    "Fraction of total team size", "Availability", "Stability",
    "Skills", "Knowledge retention consideration",
  ],
  "team::miscellaneous": [
    "Any items pertaining to Team characteristics not covered above",
  ],

  // 6. Subcontractors
  "subcontractors::organization": [
    "Size, infrastructure, financial position",
    "Past experience with our organization",
    "Past experience on similar application and technology",
    "Skill set of assigned resources",
  ],
  "subcontractors::delivery": [
    "Current cost overrun", "Current schedule slippage",
    "Current quality of deliverables",
  ],
  "subcontractors::process maturity": [
    "Formal process maturity level/certification",
    "Software engineering methods", "Project management methods",
    "Quality assurance methods", "SCM methods",
  ],
  "subcontractors::communication": [
    "Interaction frequency", "Communication channels",
    "Geographic Proximity",
  ],
  "subcontractors::miscellaneous": [
    "Any items pertaining to Subcontractor characteristics not covered above",
  ],

  // 7. Process
  "process::plans and estimates": [
    "Estimation methodology used",
    "Assumptions made for estimation",
    "Availability of past productivity data",
    "Timeliness of planning / replanning",
  ],
  "process::lifecycle": [
    "Development / maintenance lifecycle chosen: Proven lifecycle in the Organization/Industry",
    "New trial",
  ],
  "process::shortcuts": [
    "Shortcuts taken on upstream activities may result in downstream rework and increased defects",
    "Deviations and non-compliance issues",
  ],
  "process::standards": [
    "Well defined development standards from QOL/Industry",
    "Project specific development standards",
  ],
  "process::tracking": [
    "Number of management reports", "Duplication of data",
    "Too much of formality", "Amount of paperwork", "Status Reviews",
  ],
  "process::measurements and analysis": [
    "Right measures reflecting the project situation",
    "Timeliness of collection", "Timeliness of analysis",
    "Timeliness of actions",
  ],
  "process::miscellaneous": [
    "Any items pertaining to Technical and Management Process characteristics not covered above",
  ],

  // 8. Product
  "product::analysis": [
    "Proven technique", "Prior knowledge, experience and training",
    "State of functional documents / test plans",
    "Quality of Peer Review",
  ],
  "product::design": [
    "Architecture", "Distributed processing", "Data communication",
    "Performance/volume considerations",
    "State of technical documents / test plans",
    "Quality of Peer Review",
  ],
  "product::integration": [
    "Code and integrate v/s integrate at once",
    "Number of components/modules to be integrated",
  ],
  "product::testing": [
    "Availability of near production environment for testing (Hardware/System Software; Interfaces; Business Cases)",
    "Performance/volume considerations",
    "Well defined acceptance criteria for testing",
  ],
  "product::complexity": [
    "Complexity of Processing: Workflow",
    "Complexity of Processing: Logical",
    "Complexity of Processing: Mathematical",
    "Complexity of Processing: Multilingual",
  ],
  "product::quality": [
    "Current level of quality vis-a-vis the expected (based on review comments, number of errors, number of IUT rounds)",
    "Legacy defects",
    "Size and Quality of Reusable Components",
  ],
  "product::miscellaneous": [
    "Any items pertaining to Product characteristics not covered above",
  ],

  // 9. Environment
  "environment::hardware stability": [
    "Maturity of Hardware",
    "Hardware platform usage within the organization",
    "Availability of hardware support in India/Customer's Country",
    "Hardware documentation",
  ],
  "environment::hardware compatibility and connectivity": [
    "Number of new hardware technologies",
    "Compatibility of development and production environment",
    "Availability of application development tools",
    "Features/Limitation for required application development",
    "Connectivity to other computers",
    "Compliance with standards",
  ],
  "environment::system software stability": [
    "Maturity of System Software",
    "System software experience within the organization",
    "Availability of system software support in India/Customer's Country",
    "System software documentation",
  ],
  "environment::system software compatibility and connectivity": [
    "Number of new system software technologies",
    "Compatibility of development and production environment",
    "Availability of tools",
    "Features/Limitation for required application development",
    "Open architecture",
    "Userfriendliness and ease of use",
    "Compliance with standards",
  ],
  "environment::miscellaneous": [
    "Any items pertaining to Environment not covered above",
  ],
};

/**
 * Look up default factors for a standard contributor.
 * Returns a newline-joined string, or `""` if no match.
 */
export const getDefaultFactors = (
  areaName: string,
  contributorName: string
): string => {
  const key = `${areaName.trim().toLowerCase()}::${contributorName.trim().toLowerCase()}`;
  const factors = FACTORS_MAP[key];
  return factors ? factors.join("\n") : "";
};

/**
 * Look up default factors as a string array.
 * Returns an empty array if no match.
 */
export const getDefaultFactorsList = (
  areaName: string,
  contributorName: string
): string[] => {
  const key = `${areaName.trim().toLowerCase()}::${contributorName.trim().toLowerCase()}`;
  return FACTORS_MAP[key] ?? [];
};
